<template>
    <div class="h-100 d-flex flex-column">
        <h3 class="d-flex flex-row gap-2 align-items-center">
            {{ t('global.preference', 2) }}
            <small class="text-muted">
                {{ getUser().name }}
            </small>
            <button type="button" class="btn btn-outline-success btn-sm" @click="savePreferences()">
                <i class="fas fa-fw fa-save"></i>
                {{ t('global.save') }}
            </button>
        </h3>
        <div class="table-responsive scroll-x-hidden">
            <table class="table table-light table-striped table-hover mb-0" v-if="state.prefsLoaded" v-dcan="'thesaurus_write'">
                <thead class="sticky-top">
                    <tr class="text-nowrap">
                        <th>{{ t('global.preference') }}</th>
                        <th style="width: 99%;" class="text-end">{{ t('global.value') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <strong>
                                {{ t('settings.preference.key.language') }}
                            </strong>
                        </td>
                        <td>
                            <gui-language-preference
                                :data="state.preferences['prefs.gui-language']"
                                :readonly="!state.overrides['prefs.gui-language']"
                                :browser-default="true"
                                @changed="e => trackChanges('prefs.gui-language', e)">
                            </gui-language-preference>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>{{ t('settings.preference.key.password_reset_link') }}</strong>
                        </td>
                        <td>
                            <reset-email-preference
                                :data="state.preferences['prefs.enable-password-reset-link']"
                                :readonly="!state.overrides['prefs.enable-password-reset-link']"
                                @changed="e => trackChanges('prefs.enable-password-reset-link', e)">
                            </reset-email-preference>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>{{ t('settings.preference.key.project_name') }}</strong>
                        </td>
                        <td>
                            <project-name-preference
                                :data="state.preferences['prefs.project-name']"
                                :readonly="!state.overrides['prefs.project-name']"
                                @changed="e => trackChanges('prefs.project-name', e)">
                            </project-name-preference>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>{{ t('settings.preference.key.link_spacialist') }}</strong>
                        </td>
                        <td>
                            <spacialist-link-preference
                                :data="state.preferences['prefs.link-to-spacialist']"
                                :readonly="!state.overrides['prefs.link-to-spacialist']"
                                @changed="e => trackChanges('prefs.link-to-spacialist', e)">
                            </spacialist-link-preference>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>{{ t('settings.preference.key.import_config') }}</strong>
                        </td>
                        <td>
                            <import-config-preference
                                :data="state.preferences['prefs.import-config']"
                                :readonly="!state.overrides['prefs.import-config']"
                                @changed="e => trackChanges('prefs.import-config', e)">
                            </import-config-preference>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    import {
        computed,
        reactive,
    } from 'vue';

    import {
        useRoute,
    } from 'vue-router';

    import { useI18n } from 'vue-i18n';

    import store from '@/bootstrap/store.js';

    import { useToast } from '@/plugins/toast.js';

    import { patchPreferences } from '@/api.js';

    import {
        can,
        getUser,
    } from '@/helpers/helpers.js';

    import GuiLanguage from './preferences/GuiLanguage.vue';
    import ResetEmail from './preferences/ResetEmail.vue';
    import ProjectName from './preferences/ProjectName.vue';
    import SpacialistLink from './preferences/SpacialistLink.vue';
    import ImportConfig from './preferences/ImportConfig.vue';

    export default {
        components: {
            'gui-language-preference': GuiLanguage,
            'reset-email-preference': ResetEmail,
            'project-name-preference': ProjectName,
            'spacialist-link-preference': SpacialistLink,
            'import-config-preference': ImportConfig,
        },
        setup(props, context) {
            const { t, locale } = useI18n();
            const route = useRoute();
            const toast = useToast();

            // FUNCTIONS
            const trackChanges = (label, data) => {
                state.preferences[label] = data.value;
                state.dirtyData[label] = {
                    value: data.value,
                };
            };
            const savePreferences = _ => {
                if(!state.hasDirtyData) return;

                let entries = [];
                let updatedLanguage = null;
                for(let k in state.dirtyData) {
                    const dd = state.dirtyData[k];
                    if(k == 'prefs.gui-language') {
                        updatedLanguage = dd.value;
                    }
                    entries.push({
                        value: dd.value,
                        label: k,
                    });
                }
                const data = {
                    changes: entries,
                };
                patchPreferences(data, route.params.id).then(data => {
                    // Update language if value has changed
                    if(!!updatedLanguage) {
                        locale.value = updatedLanguage;
                    }
                    state.dirtyData = {};

                    const label = t('settings.preference.toasts.updated.msg');
                    toast.$toast(label, '', {
                        channel: 'success',
                        simple: true,
                    });
                });
            };

            // DATA
            const state = reactive({
                dirtyData: {},
                hasDirtyData: computed(_ => Object.keys(state.dirtyData).length > 0),
                preferences: computed(_ => store.getters.preferences),
                overrides: computed(_ => {
                    const sysPrefs = store.getters.systemPreferences;
                    const overrideList = {};
                    for(let k in sysPrefs) {
                        overrideList[k] = sysPrefs[k].allow_override;
                    }
                    return overrideList;
                }),
                prefsLoaded: computed(_ => !!state.preferences),
                browserLanguage: navigator.language ? navigator.language.split('-')[0] : 'en',
            });

            // RETURN
            return {
                t,
                // HELPERS
                can,
                getUser,
                // LOCAL
                trackChanges,
                savePreferences,
                // PROPS
                // STATE
                state,
            };
        },
    }
</script>
